import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type SignalComparable from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalComparable";
import signalsValidationService from "app/pages/sales-intelligence/sub-modules/signals/services/validation/signalsValidationService";
import { selectSignalsGroupedByType } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

type HookResult<F extends SignalFilters> = [boolean, (filters: Partial<F>) => void];

const useSignalValidation = <F extends SignalFilters>(signalPredicate: SignalComparable<F>): HookResult<F> => {
    const [isSignalValid, setIsSignalValid] = useState(true);

    const signalsGroupedByType = useSelector(selectSignalsGroupedByType);

    const signalsOfType = useMemo(
        () => signalsValidationService.getSignalsOfType(signalPredicate, signalsGroupedByType),
        [signalPredicate, signalsGroupedByType],
    );

    const isSignalDuplicate = signalsValidationService.checkIsSignalDuplicateBy(
        signalPredicate.signalType,
        signalPredicate.signalSource,
    );

    const handleSignalValidation = (filters: Partial<F>) => {
        const isDuplicate = isSignalDuplicate(filters, signalsOfType);

        setIsSignalValid(!isDuplicate);
    };

    useEffect(() => {
        handleSignalValidation(signalPredicate.filters);
    }, [signalPredicate.filters, signalPredicate.signalSubType]);

    return [isSignalValid, handleSignalValidation];
};

export default useSignalValidation;
